import { Dropdown, DropdownHandle } from 'components/dropdown/Dropdown'
import { SelectCountry, SelectCountryPicker } from 'components/selectCountry/SelectCountry'
import { ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { CountrySelectComponentProps, getCountryCallingCode } from 'react-phone-number-input'

// react-phone-number-input uses the literal 'ZZ' as its "International" sentinel (no specific country).
// The library doesn't export a constant, so we mirror the literal to detect a cleared selection.
const COUNTRY_INTL = 'ZZ'

type CountryOption = NonNullable<CountrySelectComponentProps['options']>[number]

function normalizeCountrySearch(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^\+/, '')
    .trim()
    .toLowerCase()
}

function getCountryCallingCodeSafe(value?: string) {
  if (!value || value === COUNTRY_INTL) return ''

  try {
    return getCountryCallingCode(value as Parameters<typeof getCountryCallingCode>[0]).toString()
  } catch {
    return ''
  }
}

type NormalizedCountryOption = { option: CountryOption; tokens: string[] }

function buildNormalizedOptions(
  options: CountryOption[] | undefined
): NormalizedCountryOption[] | undefined {
  return options?.map((option) => ({
    option,
    tokens: option.value
      ? [
          normalizeCountrySearch(option.label),
          normalizeCountrySearch(getCountryCallingCodeSafe(option.value)),
        ]
      : [],
  }))
}

function filterNormalizedOptions(
  normalized: NormalizedCountryOption[] | undefined,
  search: string
) {
  const query = normalizeCountrySearch(search)
  if (!query) return normalized?.map(({ option }) => option)

  return normalized
    ?.filter(({ option, tokens }) => option.value && tokens.some((t) => t.includes(query)))
    .map(({ option }) => option)
}

export function filterCountryOptions(options: CountryOption[] | undefined, search: string) {
  return filterNormalizedOptions(buildNormalizedOptions(options), search)
}

export const CountrySelectComponent = (
  props: CountrySelectComponentProps & {
    iconComponent?: ({ country }: { country: string }) => ReactElement
    fieldset: HTMLFieldSetElement | null
  }
) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const handleRef = useRef<DropdownHandle | null>(null)
  const listboxId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const onChange = props.onChange
  const handleChange = useCallback(
    (value: string | undefined) => {
      onChange?.(value === COUNTRY_INTL ? undefined : value)
      handleRef.current?.close()
    },
    [onChange]
  )
  const flag = props.value ? props.iconComponent?.({ country: props.value }) : undefined
  const selectedOption = props.options?.find((option) => option.value === props.value)
  const selectedCallingCode = getCountryCallingCodeSafe(props.value)
  const triggerAriaLabel = selectedOption?.label
    ? `${selectedOption.label}, +${selectedCallingCode}. Change country calling code`
    : 'Select country calling code'

  const normalizedOptions = useMemo(() => buildNormalizedOptions(props.options), [props.options])
  const filteredOptions = useMemo(
    () => filterNormalizedOptions(normalizedOptions, search),
    [normalizedOptions, search]
  )

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearch('')
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const timeoutIds = [0, 30, 90, 180].map((delay) =>
      window.setTimeout(() => {
        const input = searchInputRef.current
        if (!input) return

        const isInputFocused = document.activeElement === input
        if (isInputFocused && input.value) return

        if (!isInputFocused) {
          input.focus()
        }

        if (!input.value) {
          input.select()
        }
      }, delay)
    )

    return () => timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId))
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    function onFocusIn(e: FocusEvent) {
      const target = e.target as Node
      const popper = dropdownRef.current
      const trigger = triggerRef.current

      if (!popper || popper.contains(target) || trigger?.contains(target)) return

      handleRef.current?.close()
    }

    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [isOpen])

  return (
    <Dropdown
      ref={dropdownRef}
      referenceElement={props.fieldset}
      trigger={
        <SelectCountryPicker
          ref={triggerRef}
          flag={flag}
          aria-label={triggerAriaLabel}
          aria-controls={listboxId}
        />
      }
      disabled={props.disabled}
      closeOnBlur={false}
      closeOnEscape={false}
      openOnFocus={false}
      handleRef={handleRef}
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
      toggleOnTriggerClick
    >
      <SelectCountry
        options={filteredOptions}
        onChange={handleChange}
        searchValue={search}
        onSearchChange={setSearch}
        inputRef={searchInputRef}
        selectedValue={props.value}
        listboxId={listboxId}
        onEscape={() => {
          handleRef.current?.close()
          triggerRef.current?.focus()
        }}
        onTabOut={() => {
          handleRef.current?.close()
        }}
      />
    </Dropdown>
  )
}
